import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Activity, RefreshCcw, CircleCheck, CircleAlert, CircleHelp, Clock,
  Pause, Play, Ban, RotateCcw, ExternalLink, ChevronRight,
} from 'lucide-react';
import {
  The, DauThe, TheSoLieu, NutPhu, NhanTrangThai, ChuaCoDuLieu, DangTai,
} from '../components/ui';
import { api, LoiMayChu } from '../services/api';
import type { ChienDich, Job, McpWriteProposal, TrangThaiJob } from '../services/kieu';
import { moTaLoiJob } from '../services/jobErrors';
import { ActionCatalog } from '../components/ActionCatalog';

/**
 * Màn Tiến Trình — theo dõi chiến dịch và từng tác vụ bên trong.
 *
 * ĐIỂM NGHIỆP VỤ QUAN TRỌNG NHẤT: UNKNOWN_RESULT KHÔNG PHẢI THẤT BẠI.
 *
 * Nó nghĩa là "không chứng minh được Facebook đã nhận hay chưa" — thường do mất
 * kết nối giữa chừng. Bài có thể ĐÃ LÊN Facebook.
 *
 * Nếu hiển thị nó là "Thất bại", người dùng sẽ bấm chạy lại và bài lên lần hai.
 * Vì vậy ở đây nó có màu riêng, chữ riêng, và KHÔNG được gộp vào nhóm lỗi.
 * Máy chủ có bộ đối soát chạy nền tự xác minh lại các job này.
 */

interface Props {
  onLamMoi: () => void;
  tenantId?: string;
  fbUid?: string;
}

/** Cách hiển thị từng trạng thái. Gom một chỗ để tám màn nói giống nhau. */
const CACH_HIEN: Record<TrangThaiJob, { chu: string; mau: 'xanh' | 'xam' | 'vang' | 'do' }> = {
  PENDING: { chu: 'Đang chờ', mau: 'xam' },
  PAUSED: { chu: 'Tạm dừng', mau: 'vang' },
  SUCCESS: { chu: 'Thành công', mau: 'xanh' },
  FAILED: { chu: 'Thất bại', mau: 'do' },
  SKIPPED: { chu: 'Đã bỏ qua', mau: 'xam' },
  // Chữ phải nói đúng bản chất: chưa biết, không phải hỏng.
  UNKNOWN_RESULT: { chu: 'Chưa xác định', mau: 'vang' },
  ABORTED: { chu: 'Đã huỷ', mau: 'xam' },
  CANCELLED: { chu: 'Đã huỷ', mau: 'xam' },
  DELETED: { chu: 'Đã gỡ bài', mau: 'xam' },
  RETRY_REQUESTED: { chu: 'Đang xếp lại', mau: 'vang' },
};

function nhanTrangThai(tt: TrangThaiJob) {
  const c = CACH_HIEN[tt] || { chu: tt, mau: 'xam' as const };
  return <NhanTrangThai chu={c.chu} mau={c.mau} />;
}

/** Đổi mốc ISO thành chuỗi ngắn gọn theo giờ Việt Nam. */
function gioNgan(iso?: string | null) {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '—';
  const d = new Date(t);
  const homNay = new Date();
  const cungNgay = d.toDateString() === homNay.toDateString();
  const gio = d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
  return cungNgay ? gio : d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' }) + ' ' + gio;
}

export function ManTienTrinh({ onLamMoi, tenantId, fbUid }: Props) {
  const [tab, setTab] = useState<'jobs' | 'ai-approval' | 'catalog'>('jobs');
  const [chienDich, setChienDich] = useState<ChienDich[]>([]);
  const [congViec, setCongViec] = useState<Job[]>([]);
  const [proposals, setProposals] = useState<McpWriteProposal[]>([]);
  const [dangChon, setDangChon] = useState<string | null>(null);
  const [dangTai, setDangTai] = useState(true);
  const [dangTaiProposal, setDangTaiProposal] = useState(false);
  const [dangTaiJob, setDangTaiJob] = useState(false);
  const [loi, setLoi] = useState<string | null>(null);
  const [dangLam, setDangLam] = useState<string | null>(null);

  const tai = useCallback(async () => {
    setDangTai(true);
    // Hai lời gọi độc lập: một cái hỏng không được làm trắng cả màn.
    const [cd, cv] = await Promise.allSettled([api.chienDich(), api.congViec()]);
    if (cd.status === 'fulfilled') setChienDich(cd.value.campaigns || []);
    if (cv.status === 'fulfilled') setCongViec(cv.value.jobs || []);

    const hong = [cd, cv].find(x => x.status === 'rejected') as PromiseRejectedResult | undefined;
    setLoi(hong ? (hong.reason instanceof LoiMayChu ? hong.reason.message : 'Một phần dữ liệu chưa tải được.') : null);
    setDangTai(false);
  }, []);

  useEffect(() => { tai(); }, [tai]);

  const taiProposal = useCallback(async () => {
    setDangTaiProposal(true);
    try {
      const kq = await api.mcpWriteProposals('PENDING_APPROVAL', 100);
      setProposals(kq.proposals || []);
      setLoi(null);
    } catch (e) {
      setLoi(e instanceof LoiMayChu ? e.message : 'Không tải được danh sách đề xuất AI.');
    } finally {
      setDangTaiProposal(false);
    }
  }, []);

  useEffect(() => { if (tab === 'ai-approval') taiProposal(); }, [tab, taiProposal]);

  /** Mở một chiến dịch: tải riêng danh sách tác vụ của nó. */
  const chonChienDich = useCallback(async (id: string) => {
    if (dangChon === id) { setDangChon(null); return; }
    setDangChon(id);
    setDangTaiJob(true);
    try {
      const kq = await api.congViecTheoChienDich(id);
      setCongViec(kq.jobs || []);
      setLoi(null);
    } catch (e) {
      setLoi(e instanceof LoiMayChu ? e.message : 'Không tải được danh sách tác vụ.');
    } finally {
      setDangTaiJob(false);
    }
  }, [dangChon]);

  /** Tạm dừng / chạy tiếp / huỷ cả chiến dịch. */
  const dieuKhien = async (id: string, hanhDong: 'pause' | 'resume' | 'abort') => {
    const xacNhan = hanhDong === 'abort'
      ? 'Huỷ chiến dịch này? Các tác vụ chưa chạy sẽ bị dừng hẳn.'
      : null;
    if (xacNhan && !window.confirm(xacNhan)) return;

    setDangLam(id + hanhDong);
    try {
      await api.dieuKhienChienDich(id, hanhDong);
      await tai();
      onLamMoi();
    } catch (e) {
      setLoi(e instanceof LoiMayChu ? e.message : 'Không thực hiện được thao tác.');
    } finally {
      setDangLam(null);
    }
  };

  /** Chạy lại hoặc huỷ một tác vụ lẻ. */
  const dieuKhienJob = async (id: string, hanhDong: 'retry' | 'cancel') => {
    setDangLam(id + hanhDong);
    try {
      await api.dieuKhienCongViec(id, hanhDong);
      if (dangChon) await chonChienDich(dangChon);
      else await tai();
    } catch (e) {
      setLoi(e instanceof LoiMayChu ? e.message : 'Không thực hiện được thao tác.');
    } finally {
      setDangLam(null);
    }
  };

  const duyetProposal = async (p: McpWriteProposal) => {
    setDangLam(p.proposalId + 'approve');
    try {
      await api.approveMcpWriteProposal(p.proposalId, p.approvalOperationId);
      await taiProposal();
    } catch (e) {
      setLoi(e instanceof LoiMayChu ? e.message : 'Không duyệt được đề xuất AI.');
    } finally {
      setDangLam(null);
    }
  };

  const tuChoiProposal = async (p: McpWriteProposal) => {
    const reason = window.prompt('Lý do từ chối đề xuất này?', 'Không phù hợp chiến dịch') || '';
    setDangLam(p.proposalId + 'reject');
    try {
      await api.rejectMcpWriteProposal(p.proposalId, reason);
      await taiProposal();
    } catch (e) {
      setLoi(e instanceof LoiMayChu ? e.message : 'Không từ chối được đề xuất AI.');
    } finally {
      setDangLam(null);
    }
  };

  // Số liệu tổng: cộng từ thống kê chiến dịch để không phải tải hết job.
  const tong = useMemo(() => {
    const t = { dangCho: 0, thanhCong: 0, loi: 0, chuaRo: 0 };
    for (const c of chienDich) {
      t.dangCho += (c.pending || 0) + (c.paused || 0);
      t.thanhCong += c.success || 0;
      t.loi += (c.failed || 0) + (c.aborted || 0);
      // Tách riêng: đây KHÔNG phải lỗi, chỉ là chưa xác minh được.
      t.chuaRo += c.unknown || 0;
    }
    return t;
  }, [chienDich]);

  const jobHienThi = dangChon
    ? congViec
    : congViec.slice(0, 50);   // chưa chọn chiến dịch thì chỉ xem 50 việc gần nhất

  return (
    <div className="mx-auto max-w-[1440px] animate-fade-in space-y-5">
      {loi && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400">
          {loi}
        </div>
      )}

      {/* Bốn thẻ số liệu. "Chưa xác định" đứng riêng, không gộp vào lỗi.
          Cả bốn đều cộng dồn từ `chienDich` — mảng khởi tạo rỗng — nên trong lúc
          chờ API chúng đồng loạt hiện 0. Ở màn này hiểu nhầm còn nguy hơn Tổng quan:
          "Thất bại 0" và "Chưa xác định 0" là lời trấn an sai, người dùng tưởng
          mọi việc trôi chảy nên đóng màn đi mất. */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <TheSoLieu icon={<Clock className="h-5 w-5" />} so={tong.dangCho} nhan="Đang chờ chạy" dangTai={dangTai} />
        <TheSoLieu icon={<CircleCheck className="h-5 w-5" />} so={tong.thanhCong} nhan="Đã đăng thành công" dangTai={dangTai} />
        <TheSoLieu icon={<CircleAlert className="h-5 w-5" />} so={tong.loi} nhan="Thất bại / đã huỷ" dangTai={dangTai} />
        <TheSoLieu icon={<CircleHelp className="h-5 w-5" />} so={tong.chuaRo} nhan="Chưa xác định" dangTai={dangTai} />
      </div>

      {tong.chuaRo > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400">
          <span className="font-semibold">{tong.chuaRo} tác vụ chưa xác định.</span>{' '}
          Mất kết nối giữa chừng nên chưa biết Facebook đã nhận hay chưa — bài có thể đã lên.
          Hệ thống đang tự đối soát lại, bạn không cần chạy lại để tránh đăng trùng.
        </div>
      )}

      <div className="flex flex-wrap gap-2 rounded-xl border border-slate-200 bg-white p-1 shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <button
          onClick={() => setTab('jobs')}
          className={'rounded-lg px-4 py-2 text-sm font-semibold transition ' + (tab === 'jobs' ? 'bg-brand-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700/60')}
        >
          Lịch sử Job
        </button>
        <button
          onClick={() => setTab('ai-approval')}
          className={'rounded-lg px-4 py-2 text-sm font-semibold transition ' + (tab === 'ai-approval' ? 'bg-amber-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700/60')}
        >
          Chờ duyệt AI{proposals.length > 0 ? ` (${proposals.length})` : ''}
        </button>
        <button
          onClick={() => setTab('catalog')}
          className={'rounded-lg px-4 py-2 text-sm font-semibold transition ' + (tab === 'catalog' ? 'bg-brand-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700/60')}
        >
          Danh mục hành động
        </button>
      </div>

      {tab === 'catalog' ? (
        <ActionCatalog tenantId={tenantId} fbUid={fbUid} />
      ) : tab === 'ai-approval' ? (
        <DanhSachDeXuatAI
          proposals={proposals}
          dangTai={dangTaiProposal}
          dangLam={dangLam}
          onTaiLai={taiProposal}
          onDuyet={duyetProposal}
          onTuChoi={tuChoiProposal}
        />
      ) : (<>
        {/* Danh sách chiến dịch */}
        <The>
        <DauThe
          icon={<Activity className="h-4 w-4" />}
          tieuDe="Chiến dịch"
          dem={chienDich.length}
          phai={
            <NutPhu onClick={() => { setDangChon(null); tai(); onLamMoi(); }}>
              <RefreshCcw className="h-3.5 w-3.5" />
              Làm mới
            </NutPhu>
          }
        />

        {dangTai ? (
          <DangTai dong={3} />
        ) : chienDich.length === 0 ? (
          <ChuaCoDuLieu chu="Chưa có chiến dịch nào." goiY="Sang màn Tạo bài để đăng bài đầu tiên." />
        ) : (
          <div className="divide-y divide-slate-50 dark:divide-slate-700/50">
            {chienDich.map((c) => {
              const dangMo = dangChon === c.campaignId;
              const xong = (c.success || 0) + (c.failed || 0) + (c.skipped || 0);
              const phanTram = c.total > 0 ? Math.round((xong / c.total) * 100) : 0;
              const conChay = (c.pending || 0) > 0;
              const dangDung = (c.paused || 0) > 0;

              return (
                <div key={c.campaignId}>
                  <div className="flex flex-col gap-3 px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
                    <button
                      onClick={() => chonChienDich(c.campaignId)}
                      className="flex min-w-0 flex-1 items-start gap-3 text-left"
                    >
                      <ChevronRight
                        className={'mt-0.5 h-4 w-4 shrink-0 text-slate-400 transition ' + (dangMo ? 'rotate-90' : '')}
                      />
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-slate-800 dark:text-slate-100">
                          {c.title || c.message?.slice(0, 60) || '(không có tiêu đề)'}
                        </p>
                        <p className="mt-0.5 text-xs text-slate-400">
                          {gioNgan(c.createdAt)} · {c.total} tác vụ
                          {c.unknown > 0 && <> · <span className="text-amber-600 dark:text-amber-400">{c.unknown} chưa xác định</span></>}
                        </p>
                      </div>
                    </button>

                    <div className="flex shrink-0 flex-wrap items-center gap-3 pl-7 lg:pl-0">
                      {/* Thanh tiến độ: xanh phần xong, vàng phần chưa rõ */}
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-24 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
                          <div className="h-full bg-emerald-500" style={{ width: phanTram + '%' }} />
                        </div>
                        <span className="text-xs font-semibold tabular-nums text-slate-500 dark:text-slate-400">
                          {c.success}/{c.total}
                        </span>
                      </div>

                      <div className="flex gap-1.5">
                        {conChay && (
                          <NutPhu
                            onClick={() => dieuKhien(c.campaignId, 'pause')}
                            disabled={dangLam === c.campaignId + 'pause'}
                          >
                            <Pause className="h-3.5 w-3.5" />
                            Tạm dừng
                          </NutPhu>
                        )}
                        {dangDung && (
                          <NutPhu
                            onClick={() => dieuKhien(c.campaignId, 'resume')}
                            disabled={dangLam === c.campaignId + 'resume'}
                          >
                            <Play className="h-3.5 w-3.5" />
                            Chạy tiếp
                          </NutPhu>
                        )}
                        {(conChay || dangDung) && (
                          <NutPhu
                            onClick={() => dieuKhien(c.campaignId, 'abort')}
                            disabled={dangLam === c.campaignId + 'abort'}
                          >
                            <Ban className="h-3.5 w-3.5" />
                            Huỷ
                          </NutPhu>
                        )}
                      </div>
                    </div>
                  </div>

                  {dangMo && (
                    <div className="border-t border-slate-100 bg-slate-50/50 dark:border-slate-700/60 dark:bg-slate-900/20">
                      {dangTaiJob ? (
                        <DangTai dong={2} />
                      ) : jobHienThi.length === 0 ? (
                        <ChuaCoDuLieu chu="Chiến dịch này chưa có tác vụ nào." />
                      ) : (
                        <BangCongViec
                          danhSach={jobHienThi}
                          dangLam={dangLam}
                          onDieuKhien={dieuKhienJob}
                        />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        </The>

        {/* Tác vụ gần đây — chỉ hiện khi chưa mở chiến dịch nào */}
        {!dangChon && (
          <The>
          <DauThe
            icon={<Clock className="h-4 w-4" />}
            tieuDe="Tác vụ gần đây"
            dem={jobHienThi.length}
          />
          {dangTai ? (
            <DangTai dong={3} />
          ) : jobHienThi.length === 0 ? (
            <ChuaCoDuLieu chu="Chưa có tác vụ nào." />
          ) : (
            <BangCongViec danhSach={jobHienThi} dangLam={dangLam} onDieuKhien={dieuKhienJob} />
          )}
          </The>
        )}
      </>)}
    </div>
  );
}

function noiDungPreview(p: McpWriteProposal) {
  const value = p.payloadPreview.contentPreview || p.payloadPreview.content || '';
  return typeof value === 'string' && value.trim() ? value.trim() : '(không có nội dung preview)';
}

function soTarget(p: McpWriteProposal) {
  if (typeof p.inputSummary.targetCount === 'number') return p.inputSummary.targetCount;
  return Array.isArray(p.payloadPreview.targets) ? p.payloadPreview.targets.length : 0;
}

function DanhSachDeXuatAI({ proposals, dangTai, dangLam, onTaiLai, onDuyet, onTuChoi }: {
  proposals: McpWriteProposal[];
  dangTai: boolean;
  dangLam: string | null;
  onTaiLai: () => void;
  onDuyet: (p: McpWriteProposal) => void;
  onTuChoi: (p: McpWriteProposal) => void;
}) {
  return (
    <The>
      <DauThe
        icon={<CircleHelp className="h-4 w-4" />}
        tieuDe="Chờ duyệt AI"
        dem={proposals.length}
        phai={<NutPhu onClick={onTaiLai}><RefreshCcw className="h-3.5 w-3.5" />Tải lại</NutPhu>}
      />
      {dangTai ? <DangTai dong={3} /> : proposals.length === 0 ? (
        <ChuaCoDuLieu chu="Không có đề xuất AI nào đang chờ duyệt." goiY="AI chưa thể đăng nếu chưa được người dùng duyệt." />
      ) : (
        <div className="grid gap-4 p-5 lg:grid-cols-2">
          {proposals.map(p => (
            <div key={p.proposalId} className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4 dark:border-amber-500/30 dark:bg-amber-500/10">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <NhanTrangThai chu="Chờ duyệt" mau="vang" />
                <span className="font-mono text-[11px] text-slate-400">{p.proposalId}</span>
              </div>
              <p className="mt-3 whitespace-pre-line text-sm font-semibold leading-6 text-slate-900 dark:text-slate-100">{noiDungPreview(p)}</p>
              <div className="mt-3 grid gap-2 text-xs text-slate-600 dark:text-slate-300 sm:grid-cols-2">
                <div><span className="font-semibold">AI:</span> {p.createdByAiUserId}</div>
                <div><span className="font-semibold">Hạn chót:</span> {gioNgan(p.expiresAt)}</div>
                <div><span className="font-semibold">Actor:</span> {p.actorUid}</div>
                <div><span className="font-semibold">Target:</span> {soTarget(p)} nơi</div>
                <div><span className="font-semibold">Tool:</span> {p.toolName}</div>
                <div><span className="font-semibold">Tạo lúc:</span> {gioNgan(p.createdAt)}</div>
              </div>
              {Array.isArray(p.payloadPreview.targets) && p.payloadPreview.targets.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {p.payloadPreview.targets.slice(0, 6).map((t, i) => (
                    <span key={`${t.id || i}`} className="rounded-full bg-white px-2 py-1 text-[11px] font-mono text-slate-500 dark:bg-slate-900 dark:text-slate-300">{t.name || t.id || 'target'}</span>
                  ))}
                </div>
              )}
              <div className="mt-4 rounded-xl border border-amber-200 bg-white p-3 text-xs text-amber-800 dark:border-amber-500/30 dark:bg-slate-950 dark:text-amber-300">
                Duyệt sẽ tạo job thật và đưa vào hàng đợi Facebook bằng actor đã được trói với phiên MCP.
              </div>
              <div className="mt-4 grid gap-2 sm:grid-cols-2">
                <button
                  onClick={() => onDuyet(p)}
                  disabled={dangLam === p.proposalId + 'approve'}
                  className="rounded-xl bg-emerald-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-emerald-700 disabled:opacity-60"
                >
                  Duyệt và đưa vào hàng đợi
                </button>
                <button
                  onClick={() => onTuChoi(p)}
                  disabled={dangLam === p.proposalId + 'reject'}
                  className="rounded-xl bg-rose-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-rose-700 disabled:opacity-60"
                >
                  Từ chối
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </The>
  );
}

/** Bảng tác vụ. Tách riêng vì dùng ở hai chỗ. */
function BangCongViec({
  danhSach, dangLam, onDieuKhien,
}: {
  danhSach: Job[];
  dangLam: string | null;
  onDieuKhien: (id: string, hanhDong: 'retry' | 'cancel') => void;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] text-left text-sm">
        <thead>
          <tr className="border-b border-slate-100 text-[11px] uppercase tracking-wide text-slate-400 dark:border-slate-700/60">
            <th className="px-5 py-2.5 font-semibold">Nội dung</th>
            <th className="px-3 py-2.5 font-semibold">Nhóm</th>
            <th className="px-3 py-2.5 font-semibold">Cập nhật</th>
            <th className="px-3 py-2.5 font-semibold">Trạng thái</th>
            <th className="px-5 py-2.5 text-right font-semibold">Thao tác</th>
          </tr>
        </thead>
        <tbody>
          {danhSach.map((j) => {
            // Chỉ cho chạy lại khi đã có kết quả cuối và KHÔNG phải chưa xác định.
            // UNKNOWN_RESULT mà chạy lại là nguy cơ đăng trùng bài.
            const choChayLai = j.status === 'FAILED' || j.status === 'ABORTED' || j.status === 'CANCELLED';
            const choHuy = j.status === 'PENDING' || j.status === 'PAUSED';

            return (
              <tr key={j.id} className="border-b border-slate-50 last:border-0 dark:border-slate-700/50">
                <td className="max-w-[320px] px-5 py-3">
                  <p className="truncate font-medium text-slate-800 dark:text-slate-100">
                    {j.title || j.message?.slice(0, 70) || '(không có nội dung)'}
                  </p>
                  {j.errorDetail && (
                    <p className="mt-0.5 truncate text-xs text-rose-600 dark:text-rose-400" title={j.errorDetail}>
                      {moTaLoiJob(j.errorDetail)}
                    </p>
                  )}
                </td>
                <td className="px-3 py-3 font-mono text-xs text-slate-500 dark:text-slate-400">
                  {j.targetId || '—'}
                </td>
                <td className="px-3 py-3 text-xs text-slate-500 dark:text-slate-400">
                  {gioNgan(j.updatedAt || j.createdAt)}
                </td>
                <td className="px-3 py-3">{nhanTrangThai(j.status)}</td>
                <td className="px-5 py-3">
                  <div className="flex items-center justify-end gap-1.5">
                    {j.evidenceUrl && (
                      <a
                        href={j.evidenceUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700/50"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        Xem bài
                      </a>
                    )}
                    {choChayLai && (
                      <NutPhu onClick={() => onDieuKhien(j.id, 'retry')} disabled={dangLam === j.id + 'retry'}>
                        <RotateCcw className="h-3.5 w-3.5" />
                        Chạy lại
                      </NutPhu>
                    )}
                    {choHuy && (
                      <NutPhu onClick={() => onDieuKhien(j.id, 'cancel')} disabled={dangLam === j.id + 'cancel'}>
                        <Ban className="h-3.5 w-3.5" />
                        Huỷ
                      </NutPhu>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
